import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { IMG_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const ReportsList = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BASE_URL}reports`)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 1) {
          setReports(data.data);
          setFilteredReports(data.data);
        } else {
          alert("Failed to fetch reports");
        }
      })
      .catch((error) => console.error("Error fetching reports:", error));
  }, []);

  // Function to get date from report
  const getReportDate = (report) => {
    // Try different possible date fields
    if (report.date) return report.date;
    if (report.created_at) return report.created_at;
    if (report.updated_at) return report.updated_at;
    return null;
  };

  // Apply all filters - ONLY when button is clicked
  const applyFilters = () => {
    let filtered = [...reports];

    // Apply search filter
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((report) =>
        report.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply date range filter
    if (startDate || endDate) {
      filtered = filtered.filter((report) => {
        const reportDate = getReportDate(report);
        if (!reportDate) return false; // Exclude if no date

        const reportDateObj = new Date(reportDate);
        const startDateObj = startDate ? new Date(startDate) : null;
        const endDateObj = endDate ? new Date(endDate) : null;

        // Compare only date part (ignore time)
        const reportDateOnly = new Date(
          reportDateObj.getFullYear(),
          reportDateObj.getMonth(),
          reportDateObj.getDate()
        );
        const startDateOnly = startDateObj
          ? new Date(
              startDateObj.getFullYear(),
              startDateObj.getMonth(),
              startDateObj.getDate()
            )
          : null;
        const endDateOnly = endDateObj
          ? new Date(
              endDateObj.getFullYear(),
              endDateObj.getMonth(),
              endDateObj.getDate()
            )
          : null;

        let isValid = true;

        if (startDateOnly && reportDateOnly < startDateOnly) {
          isValid = false;
        }

        if (endDateOnly && reportDateOnly > endDateOnly) {
          isValid = false;
        }

        return isValid;
      });
    }

    setFilteredReports(filtered);
  };

  // REMOVE THIS AUTO-FILTER USEEFFECT COMPLETELY
  // useEffect(() => {
  //   applyFilters();
  // }, [searchTerm, startDate, endDate, reports]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    // DON'T call applyFilters() here
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    // DON'T call applyFilters() here
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    // DON'T call applyFilters() here
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This report will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${BASE_URL}reports/${id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status === 1) {
              setReports((prev) => prev.filter((report) => report.id !== id));
              Swal.fire("Deleted!", "The report has been deleted.", "success");
            } else {
              Swal.fire(
                "Error",
                data.message || "Failed to delete the report.",
                "error"
              );
            }
          })
          .catch((error) => {
            console.error("Delete error:", error);
            Swal.fire("Error", "Something went wrong while deleting.", "error");
          });
      }
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB"); // DD/MM/YYYY
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Safe JSON parsing for images
  const parseImages = (imagesString) => {
    try {
      if (!imagesString) return [];
      return JSON.parse(imagesString);
    } catch (error) {
      console.error("Error parsing images:", error);
      return [];
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Reports List", 14, 10);

    const tableData = filteredReports.map((report) => [
      report.id,
      report.name || "N/A",
      report.description || "No Description",
      JSON.parse(report.images || "[]").join(", ") || "No Image",
    ]);

    doc.autoTable({
      head: [["ID", "Name", "Description", "Images"]],
      body: tableData,
    });

    doc.save("reports-list.pdf");
  };

  // Export to Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredReports.map((report) => ({
        ID: report.id,
        Name: report.name || "N/A",
        Description: report.description || "No Description",
        Images: JSON.parse(report.images || "[]").join(", ") || "No Image",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports List");
    XLSX.writeFile(workbook, "reports-list.xlsx");
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Reports List</h3>
            </div>
            <div className="col-12 col-md-6 order-md-2 order-first">
              <nav
                aria-label="breadcrumb"
                className="breadcrumb-header float-start float-lg-end"
              >
                <ol className="breadcrumb"></ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row" id="basic-table">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-3">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search report name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={handleStartDateChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={handleEndDateChange}
                    />
                  </div>

                  <div className="col-md-3 text-end">
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                    {/* <button
                      className="btn btn-secondary me-2"
                      onClick={handleExportExcel}
                    >
                      Export to Excel
                    </button> */}
                  </div>
                </div>

                {/* ADD THIS ROW FOR THE APPLY FILTER BUTTON */}
                <div className="row mt-3">
                  <div className="col-md-2">
                    <button
                      className="btn btn-secondary me-2"
                      onClick={applyFilters}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>NAME</th>
                        <th>DESCRIPTION</th>
                        <th>IMAGES</th>
                        <th className="text-end">ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReports.length > 0 ? (
                        filteredReports.map((report) => (
                          <tr key={report.id}>
                            <td>{report.id}</td>
                            <td>{report.name || "N/A"}</td>
                            <td>{report.description || "No Description"}</td>
                            <td>
                              {parseImages(report.images).map(
                                (image, index) => (
                                  <img
                                    key={index}
                                    src={`${IMG_URL}${image}`}
                                    alt={`Report Image ${index + 1}`}
                                    style={{
                                      width: "100px",
                                      height: "auto",
                                      marginRight: "5px",
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src =
                                        "https://via.placeholder.com/100x100?text=No+Image";
                                    }}
                                  />
                                )
                              )}
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-danger ms-2"
                                onClick={() => handleDelete(report.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No reports found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReportsList;
